<template>
    <div :id="editorId" ref="editorContainer" class="editor-container" :class="{ 'readonly-mode': readOnly }" :style="customHeight ? { height: customHeight } : {}">
        <button v-if="copyCode" type="button" class="btn btn-sm btn-link float-end copy-btn"  @click="onCopy"> <span class="fa fa-copy"></span></button>
    </div>
</template>

<script>
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine, drawSelection } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { autocompletion, closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { sql } from '@codemirror/lang-sql';
import { yaml } from '@codemirror/lang-yaml';
import { json } from '@codemirror/lang-json';
import { oneDark } from '@codemirror/theme-one-dark';
import { lintGutter, linter } from '@codemirror/lint';
import { toggleComment } from '@codemirror/comment';
import { searchKeymap, highlightSelectionMatches, search, closeSearchPanel } from '@codemirror/search';
import { indentOnInput, syntaxHighlighting, defaultHighlightStyle, bracketMatching, foldGutter, indentUnit } from '@codemirror/language';
import yamlParser from 'js-yaml';

export default {
    name: 'CodeMirrorEditor', // version 6
    props: {
        editorId: { type: String, default: 'codeEditor' },
        theme: { type: String, default: 'light' },
        mode: { type: String, default: 'javascript' },
        type: {type: String, default: "" },
        readOnly: { type: Boolean, default: false },
        refreshOn: { type: Boolean, default: false },
        modelValue: [String, Object],
        editorType: { type: String },
        performLint: { type: Boolean, default: true },
        performSilentLint: { type: Boolean, default: false },
        customLint: { type: Boolean, default: false },
        customLintErrors: { default: null },
        lineWrapping: { type: Boolean, default: false },
        copyCode: { type: Boolean, default: true },
        customHeight: { type: String, default: null },
    },
    data() {
        return {
            editorContainer: null,
            view: null,
            isRefreshed: false,
            readOnlyCompartment: new Compartment(),
        };
    },
    mounted() {
        this.editorContainer = this.$refs.editorContainer;
        const state = EditorState.create({
            doc: this.modelValue || '',
            extensions: this.getExtensions(),
        });
        this.view = new EditorView({ state, parent: this.editorContainer });
    },
    watch: {
        modelValue(val) {
            if (this.view && val !== this.view.state.doc.toString()) {
                this.view.dispatch({
                    changes: { from: 0, to: this.view.state.doc.length, insert: val || '' },
                });
            }
        },
        customLintErrors() {
            if (this.view) {
                this.view.dispatch({ effects: [] });
            }
        },
        readOnly(newVal) {
            if (this.view) {
                this.view.dispatch({
                    effects: this.readOnlyCompartment.reconfigure(EditorView.editable.of(!newVal))
                });
                if (newVal) {
                    closeSearchPanel(this.view);
                }
            }
        },
        refreshOn(val) {
            if (val && this.editor && !this.isRefreshed) {
                this.view.requestMeasure();
                this.isRefreshed = true;
            }
        }
    },
    methods: {
        getLanguageExtension() {
            const mode = this.mode.toLowerCase();
            switch (mode) {
                case 'python':
                case 'py': return python();
                case 'yaml': return yaml();
                case 'sql': return sql();
                case 'json': return json();
                default: return javascript();
            }
        },
        yamlLinter() {
            return linter((view) => {
                const diagnostics = [];
                try {
                    const cleaned = view.state.doc.toString().replace(/\*\w+\b/g, '');
                    yamlParser.loadAll(cleaned);
                } catch (e) {
                    const { mark } = e;
                    if (mark) {
                        diagnostics.push({
                            from: view.state.doc.line(mark.line + 1).from + mark.column,
                            to: view.state.doc.line(mark.line + 1).from + mark.column,
                            message: e.message,
                            severity: 'error',
                        });
                    }
                }
                
                // donot emit lint failure if need to perform lint silently
                if (!this.performSilentLint) this.$emit('lintFailure', diagnostics.length > 0);

                return diagnostics;
            });
        },
        jsonLinter() {
            return linter((view) => {
                const diagnostics = [];
                try {
                    JSON.parse(view.state.doc.toString());
                } catch (e) {
                    const match = e.message.match(/at position (\d+)/);
                    let pos = 0;
                    if (match && match[1]) {
                        pos = parseInt(match[1], 10);
                    }
                    diagnostics.push({ from: pos, to: pos, message: e.message, severity: 'error' });
                }

                // donot emit lint failure if need to perform lint silently
                if (!this.performSilentLint) this.$emit('lintFailure', diagnostics.length > 0);

                return diagnostics;
            });
        },
        customLinter() {
            return linter(() => {
                const diagnostics = [];
                if (this.customLintErrors && Array.isArray(this.customLintErrors)) {
                    for (const error of this.customLintErrors) {
                        const from = this.view.state.doc.line(error.from[0]).from + error.from[1];
                        const to = this.view.state.doc.line(error.to[0]).from + error.to[1];
                        diagnostics.push({ from, to, message: error.message, severity: error.severity || 'error' });
                    }
                }

                // donot emit lint failure if need to perform lint silently
                if (!this.performSilentLint) this.$emit('lintFailure', diagnostics.length > 0);

                return diagnostics;
            });
        },
        getExtensions() {
            const extensions = [
                lineNumbers(),
                highlightActiveLine(),
                drawSelection(),
                indentOnInput(),
                history(),
                closeBrackets(),
                bracketMatching(),
                syntaxHighlighting(defaultHighlightStyle),
                keymap.of([
                    indentWithTab,
                    ...defaultKeymap,
                    ...historyKeymap,
                    ...searchKeymap,
                    ...closeBracketsKeymap,
                    { key: 'Mod-/', run: toggleComment, preventDefault: true }
                ]),
                highlightSelectionMatches(),
                this.getLanguageExtension(),
                EditorView.updateListener.of((update) => {
                    if (update.docChanged) {
                        this.$emit('update:modelValue', update.state.doc.toString());
                    }
                }),
                this.readOnlyCompartment.of(EditorView.editable.of(!this.readOnly)),
                indentUnit.of(this.mode.toLowerCase() == "yaml" || this.mode.toLowerCase() == "json" ? "  " : "    "),
                EditorState.tabSize.of(this.mode.toLowerCase() == "yaml" || this.mode.toLowerCase() == "json" ? 2 : 4),
                autocompletion(),
                search({ top: true })
            ];

            extensions.push(foldGutter({
                markerDOM: (open) => {
                    const icon = document.createElement("span");
                    icon.style.cursor = "pointer";
                    icon.style.display = "inline-block";
                    icon.style.width = "1em";
                    icon.style.textAlign = "center";
                    icon.style.color = "#1e293b";
                    icon.style.fontSize = "0.65rem";
                    icon.textContent = open ? "\u25BC" : "\u25B6";
                    return icon;
                }
            }));

            if (this.theme === 'dark') extensions.push(oneDark);
            if (this.lineWrapping) extensions.push(EditorView.lineWrapping);

            if (this.performLint || this.performSilentLint) {
                if (this.customLint) {
                    extensions.push(lintGutter(), this.customLinter());
                } else {
                    switch (this.mode.toLowerCase()) {
                        case 'yaml': extensions.push(lintGutter(), this.yamlLinter()); break;
                        case 'json': extensions.push(lintGutter(), this.jsonLinter()); break;
                    }
                }
            }

            return extensions;
        },
        onCopy() {
            const code = this.view.state.doc.toString();
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(code).then(() => {
                    this.$store.dispatch("addToast", {
                            message: "Copied",
                            type: "success",
                    });
                }).catch(err => {
                    console.error('Copy failed', err);     
                });
            }
        }
    }
};
</script>

<style>
.editor-container {
    min-height: 25px;
    resize: vertical;
    overflow: auto;
    background-color: white;
    border: 1px solid #e2e8f0;
}

.cm-editor {
    font-size: 14.5px;
    line-height: 1.5;
    background-color: white;
    color: black;
    height: 100%;
}

.cm-gutters {
    background: #f6f6f6 !important;
    height: 100% !important;
}

.cm-gutterElement {
    color: #8d8d8d !important;
}

/* When readonly is true */
.editor-container.readonly-mode {
    background-color: #f3f7ff;
}

.editor-container.readonly-mode .cm-editor {
    background-color: #f3f7ff;
    color: #000000;
}

.editor-container.readonly-mode .cm-activeLine {
    background-color: transparent !important;
}

.cm-panel.cm-search {
    position: sticky !important;
    top: 0 !important;
    z-index: 10 !important;
    background: #f8fafc !important;
    padding: 6px 8px !important;
    border-radius: 0 !important;
    margin: 0 !important;
}

.cm-search input {
    background: white !important;
    border: 1px solid #d1d5db !important;
    border-radius: 3px !important;
    padding: 2px 10px !important;
    font-size: 12.5px !important;
    margin-right: 15px !important;
    color: #2c2c2c !important;
}

.cm-textfield {
    min-width: 200px !important;
    /* Adjust to your preferred size */
    max-width: 300px !important;
    /* Optional: limit max width */
    width: 250px !important;
    /* This sets the base width */
}

.cm-search button {
    background: #3b82f6 !important;
    color: white !important;
    border: none !important;
    border-radius: 3px !important;
    padding: 2px 10px !important;
    cursor: pointer !important;
    font-size: 12.5px !important;
    min-height: 24px !important;
}

.cm-search button[name="close"] {
    background-color: transparent !important;
    color: rgb(255, 0, 0) !important;
    font-size: 20px !important;
    margin: 0 0 !important;
}

.cm-search label {
    display: inline-flex !important;
    align-items: center !important;
    font-size: 13px !important;
    margin-left: 10px !important;
    margin-right: 6px !important;
    gap: 4px !important;
    color: #2c2c2c !important;
    vertical-align: middle !important;
}

.cm-search input[type="checkbox"] {
    margin: 0 !important;
    vertical-align: middle !important;
    position: relative;
    top: 0 !important;
}

.cm-gutter-lint {
    width: 0px !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
}

.cm-gutters {
    padding-right: 0px !important;
    width: auto !important;
}

.cm-gutterElement {
    padding-left: 8px !important;
    padding-right: 4px !important;
}
.copy-btn {
    color:#8d8d8d;
}
.copy-btn:hover {
    color: #6E84A3;
}
</style>